import { Component, ViewEncapsulation } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import {
  Campaign,
  CampaignProduct,
  CampaignSection, CancelItemsSelection,
  getCampaignProductImageUrl,
  ImageSize,
  MembershipType,
  Pager,
  ShowMarketplaceSetting,
  ShowMarketplaceSettingOptions,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService, CampaignProductService, CampaignService, LoaderService, SideNavService } from '../../_services';
import { FormBuilder, Validators } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';
import { config } from '../../../config/config';

@Component({
  templateUrl: './campaign-marketplace-settings.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-marketplace-settings.component.scss'],
})
export class CampaignMarketplaceSettingsComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.MarketplaceSettings;
  campaignProducts: CampaignProduct[] = null;
  campaignProductsModalInput: CampaignProduct[] = null;
  selectedCampaignProduct: CampaignProduct;
  showMarketplaceSettings = ShowMarketplaceSettingOptions;
  campaignProductsLoading = false;
  membershipTypes = [
    { value: MembershipType.SpecificSubscription, label: 'Specific Subscription' },
    { value: MembershipType.AnyActiveSubscription, label: 'Any Active Subscription' },
    { value: MembershipType.AnyCompletedPurchase, label: 'Any Completed Purchase' },
    { value: MembershipType.ExistingCustomer, label: 'Existing Customer' },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sideNav: SideNavService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected productService: CampaignProductService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      show_marketplace: [ShowMarketplaceSetting.HideIfEmbedded, Validators.required],
      guarantee_days: [0, [Validators.required, Validators.min(0)]],
      membership_type: [MembershipType.SpecificSubscription, Validators.required],
      membership_discount: [0, [Validators.min(0), Validators.max(100)]],
    });

    super.ngOnInit();
  }

  onCampaignFetch(data: Campaign) {
    this.getCampaignProducts();
    this.form.patchValue(data);
  }

  protected getCampaignProducts(hasCampaignProduct = false) {
    if (this.campaign) {
      if (this.campaign.campaign_products.length) {
        this.campaignProductsLoading = true;

        this.productService
          .list({ page: 1, page_size: config.maxPageSize, campaigns: this.campaign.id })
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (page: Pager) => {
              this.campaignProductsLoading = false;
              let products = page.results;

              // we got the products for this step, but now we need to sort them by step's campaign product ids
              products.sort((a: CampaignProduct, b: CampaignProduct) => {
                let productIds: any[] = this.campaign.campaign_products;

                return productIds.indexOf(a.id) - productIds.indexOf(b.id);
              });

              this.campaignProducts = products;
            },
            (error) => {
              this.campaignProductsLoading = false;
              this.handleError(error);
            }
          );
      } else if (!hasCampaignProduct) {
        this.campaignProducts = [];
      }
    }
  }

  addCampaignProduct() {
    this.campaignProductsModalInput = this.campaignProducts;
    this.modalService.getModal('selectProductsDialog').open();
  }

  onCampaignProductsSelected(products: CampaignProduct[]) {
    this.campaignProducts = products;
    this.modalService.getModal('selectProductsDialog').close();
  }

  removeCampaignProduct(productId) {
    this.campaignProducts = this.campaignProducts.filter((product: CampaignProduct) => product.id != productId);
  }

  getCampaignProductImageUrl(campaignProduct) {
    return getCampaignProductImageUrl(campaignProduct, ImageSize.small);
  }

  copyCampaignProducts() {
    this.campaignService.copyProducts(this.campaignProducts);
  }

  canPasteCampaignProducts() {
    return this.campaignService.canPasteProducts();
  }

  pasteCampaignProducts() {
    this.campaignProducts = this.campaignService.pasteProducts();
  }

  editCampaignProduct(campaignProduct: CampaignProduct) {
    this.selectedCampaignProduct = campaignProduct;
    this.openEditCampaignProductDialog();
  }

  protected openEditCampaignProductDialog() {
    this.modalService.getModal('editCampaignProductDialog').open();
  }

  onSaveCampaignProduct(newItem: CampaignProduct) {
    this.selectedCampaignProduct = null; // to re-initialize the form, otherwise the form gets invalid
    this.modalService.getModal('editCampaignProductDialog').close();
    const index = (this.campaignProducts || []).findIndex((product) => newItem.id == product.id);
    if (index === -1) {
      this.campaignProducts = [];
    }
    this.getCampaignProducts(true);
  }

  onCloseCampaignProductDialog() {
    this.selectedCampaignProduct = null;
  }

  get canEditMembershipDiscount(): boolean {
    return !this.isFieldEqual('membership_type', MembershipType.SpecificSubscription);
  }

  get isMoneyBackGuaranteeEnabled(): boolean {
    return !this.isFieldEqual('guarantee_days', 0);
  }

  allowMoneyBackGuarantee(event) {
    this.form.patchValue({
      guarantee_days: event.target.checked ? 30 : 0
    });
  }

  protected getFormData(): any {
    if (this.campaignProducts) {
      this.form.value.campaign_products = this.campaignProducts.map((product: CampaignProduct) => product.id);
    }

    return Object.assign({}, this.form.value);
  }
}
